import { Modal, Row, Col } from "antd";
import { Flex, Box, Textarea, Text } from "@chakra-ui/react";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import Field from "ui/components/Input/Field";
import { useForm } from "react-hook-form";
import Anexos from "ui/components/Anexos";
import ButtonAnexarArquivos from "ui/components/Button/ButtonAnexarArquivos";
import { useEffect } from "react";
import { EquipamentoRealizarManutencaoForm } from "core/features/manutencao/typings/index";
import ShadowSelectableButton from "components/Button/ShadowSelectableButton";

import { showSuccess, showErrorAxios } from "infra/helpers/alerta";
import { ManutencaoDto } from "core/features/manutencao/typings/index";
import { StatusManutencaoEnum } from "core/features/manutencao/typings";
import useRealizarManutencaoEquipamentos from "core/features/manutencao/hooks/useRealizarManutencaoEquipamentos";

interface ModalManutencaoEquipamentoProps {
  visible: boolean;
  onClose: () => any;

  refetchList?: () => any;
  equipamento: Partial<EquipamentoRealizarManutencaoForm> | undefined;
  manutencaoDto: ManutencaoDto | undefined;
}

export default function ModalManutencaoEquipamento({
  onClose,
  visible,
  equipamento,
  manutencaoDto,
  refetchList,
}: ModalManutencaoEquipamentoProps) {
  const { mutate: handleSave, isLoading } = useRealizarManutencaoEquipamentos(
    manutencaoDto?.id!
  );
  const { handleSubmit, control, setValue, watch, reset } =
    useForm<EquipamentoRealizarManutencaoForm>({
      defaultValues: equipamento,
    });

  const onSubmit = async (data: EquipamentoRealizarManutencaoForm) => {
    handleSave(
      { ...data, dataManutencao: manutencaoDto?.dataPrevistaInicial },
      {
        onSuccess: () => {
          setTimeout(() => {
            showSuccess().then(() => onClose());
          }, 250);
          refetchList?.();
        },
        onError: (err) => {
          showErrorAxios(err);
        },
      }
    );
  };

  useEffect(() => {
    if (visible) {
      reset(equipamento);
    }
  }, [equipamento, reset, visible]);

  const form = watch();

  return (
    <Modal
      destroyOnClose
      width={"500px"}
      onCancel={onClose}
      centered
      title={equipamento?.nome}
      visible={visible}
      footer={
        <Flex justifyContent="end" gap={4} mb={4}>
          <OutlineButton onClick={onClose}>Cancelar</OutlineButton>
          <PrimaryButton
            onClick={() => handleSubmit(onSubmit)()}
            isLoading={isLoading}
          >
            Concluir
          </PrimaryButton>
        </Flex>
      }
    >
      <form onSubmit={(e) => handleSubmit(onSubmit)(e)}>
        <Field
          name="statusManutencaoId"
          inputProps={{ type: "text" }}
          control={control}
          render={({ field }) => (
            <Box as={Row} gutter={16} mb={4}>
              <Box as={Col} md={12}>
                <ShadowSelectableButton
                  onClick={() => field.onChange(StatusManutencaoEnum.Concluida)}
                  selectedColor="green"
                  colorText="green"
                  selected={
                    form.statusManutencaoId === StatusManutencaoEnum.Concluida
                  }
                >
                  <Text>Realizado</Text>
                </ShadowSelectableButton>
              </Box>
              <Box as={Col} md={12}>
                <ShadowSelectableButton
                  onClick={() =>
                    field.onChange(StatusManutencaoEnum.NaoRealizada)
                  }
                  selectedColor="red"
                  colorText="red"
                  selected={
                    form.statusManutencaoId ===
                    StatusManutencaoEnum.NaoRealizada
                  }
                >
                  <Text>Não realizado</Text>
                </ShadowSelectableButton>
              </Box>
            </Box>
          )}
        />

        <Box mb={4}>
          <Field
            label="Observação"
            name="statusMotivo"
            inputProps={{ type: "text" }}
            control={control}
            render={({ field, isInvalid }) => (
              <Textarea
                isInvalid={isInvalid}
                className="chakra-input"
                rows={8}
                resize="none"
                placeholder="Digite..."
                {...field}
              />
            )}
          />
        </Box>

        <Box>
          <Text fontWeight="500" fontSize={14} mb={4}>
            Arquivos
          </Text>
          <Flex flex={1}>
            <ButtonAnexarArquivos
              maxFileSizeMb={200}
              currentFilesLength={form.arquivos?.length || 0}
              variant="ghost"
              onAnexar={(files) => {
                if (files) {
                  const currentAnexos = form.arquivos;

                  setValue(
                    "arquivos",
                    currentAnexos ? [...currentAnexos, ...files] : files
                  );
                }
              }}
            />
          </Flex>

          <Flex gap="16px" mt="auto" h="fit-content" py={2} flexDir="column">
            {form.arquivos?.length ? (
              <Anexos
                numColumns={2}
                setValue={(anexos) =>
                  setValue(
                    "arquivos",
                    anexos?.map((anexo) =>
                      Object.assign(anexo, { nome: anexo.name })
                    ) || []
                  )
                }
                anexos={
                  form.arquivos?.map((file) =>
                    file instanceof File ? file : { ...file, name: file.nome }
                  ) || undefined
                }
              />
            ) : null}
          </Flex>
        </Box>
      </form>
    </Modal>
  );
}
